/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Modal Component - Modal for submitting feedback
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState, useEffect, useRef } from 'react'
import { X, Camera, Upload, Send, Trash2, Star, AlertCircle, Info } from 'lucide-react'
import ModalPortal from '../ModalPortal'
import useFeedbackStore from '../../stores/feedbackStore'
import feedbackService from '../../services/feedbackService'
import { getCompleteFeedbackContext, getContextDescription } from '../../utils/feedbackContext'
import toast from 'react-hot-toast'
import { createLogger } from '../../utils/logger'

const logger = createLogger('FeedbackModal')

const CATEGORIES = [
  { value: 'bug', label: 'Bug Report', description: 'Report an error or unexpected behavior' },
  { value: 'feature_request', label: 'Feature Request', description: 'Suggest a new feature' },
  {
    value: 'content_error',
    label: 'Content Error',
    description: 'Report incorrect or unclear content'
  },
  {
    value: 'ui_ux',
    label: 'UI/UX Feedback',
    description: 'Suggest improvements to design or usability'
  },
  { value: 'performance', label: 'Performance Issue', description: 'Report slow loading or lag' },
  { value: 'other', label: 'Other', description: 'General feedback or questions' }
]

const FeedbackModal = () => {
  const { isFeedbackModalOpen, closeFeedbackModal } = useFeedbackStore()

  // Form state
  const [category, setCategory] = useState('bug')
  const [rating, setRating] = useState(null)
  const [message, setMessage] = useState('')
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotPreview, setScreenshotPreview] = useState(null)

  // UI state
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [hideModalForCapture, setHideModalForCapture] = useState(false)

  // Context information
  const [contextDescription, setContextDescription] = useState('')

  // Ref for modal element
  const modalRef = useRef(null)

  useEffect(() => {
    if (isFeedbackModalOpen) {
      setContextDescription(getContextDescription())
      // Note: screenshots are no longer auto-captured. The native screen-capture API
      // (getDisplayMedia) requires a user gesture and shows a picker, so capture only
      // happens when the user clicks "Capture Screenshot".
    } else {
      resetForm()
    }
  }, [isFeedbackModalOpen])

  // Cleanup object URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (screenshotPreview) {
        URL.revokeObjectURL(screenshotPreview)
      }
    }
  }, [screenshotPreview])

  const resetForm = () => {
    setCategory('bug')
    setRating(null)
    setMessage('')
    setScreenshot(null)
    setScreenshotPreview(null)
    setErrors({})
  }

  const captureScreenshot = async () => {
    // Native screen capture works reliably across Safari/Chrome/Firefox (unlike
    // html2canvas, which renders broken images on Safari). It requires a secure context
    // and a user gesture, which the "Capture Screenshot" button click provides.
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error(
        'Screen capture is not supported in this browser. Please upload an image instead.'
      )
      return
    }

    setIsCapturingScreenshot(true)
    let stream = null

    try {
      // Hide our modal so the captured frame shows the page underneath, not this dialog.
      setHideModalForCapture(true)

      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        // Hint supporting browsers (Chrome) to default the picker to the current tab.
        preferCurrentTab: true
      })

      // Grab a single still frame from the capture stream.
      const video = document.createElement('video')
      video.srcObject = stream
      video.muted = true
      await video.play()
      // Ensure frame dimensions are available before drawing.
      if (!video.videoWidth) {
        await new Promise((resolve) => {
          video.addEventListener('loadedmetadata', resolve, { once: true })
        })
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' })
          setScreenshot(file)
          setScreenshotPreview(URL.createObjectURL(blob))
          toast.success('Screenshot captured!')
        } else {
          toast.error('Failed to capture screenshot. Please try uploading an image instead.')
        }
      }, 'image/png')
    } catch (error) {
      // Dismissing the picker rejects with NotAllowedError/AbortError - treat as a no-op.
      if (error?.name !== 'NotAllowedError' && error?.name !== 'AbortError') {
        logger.error('Error capturing screenshot:', error)
        toast.error('Failed to capture screenshot. Please try uploading an image instead.')
      }
    } finally {
      // Always stop the capture tracks (releases the share indicator) and restore the modal.
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
      setHideModalForCapture(false)
      setIsCapturingScreenshot(false)
    }
  }

  const handleCaptureScreenshot = () => captureScreenshot()

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Screenshot must be less than 5MB')
      return
    }

    setScreenshot(file)
    setScreenshotPreview(URL.createObjectURL(file))
    toast.success('Screenshot uploaded!')
  }

  const handleRemoveScreenshot = () => {
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview)
    }
    setScreenshot(null)
    setScreenshotPreview(null)
  }

  const validateForm = () => {
    const newErrors = {}

    if (!category) {
      newErrors.category = 'Please select a category'
    }

    if (!message.trim()) {
      newErrors.message = 'Message is required'
    } else if (message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters'
    } else if (message.trim().length > 2000) {
      newErrors.message = 'Message must be less than 2000 characters'
    }

    if (rating && (rating < 1 || rating > 5)) {
      newErrors.rating = 'Rating must be between 1 and 5'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    setIsSubmitting(true)

    try {
      // Get context and metadata
      const context = getCompleteFeedbackContext()

      // Create FormData for multipart upload
      const formData = new FormData()
      formData.append('category', category)
      formData.append('message', message.trim())

      if (rating) {
        formData.append('rating', rating.toString())
      }

      if (screenshot) {
        formData.append('screenshot', screenshot)
      }

      if (context.challengeId) {
        formData.append('challengeId', context.challengeId)
      }

      if (context.stageId) {
        formData.append('stageId', context.stageId)
      }

      if (context.questionId) {
        formData.append('questionId', context.questionId)
      }

      if (context.metadata) {
        formData.append('metadata', JSON.stringify(context.metadata))
      }

      // Submit feedback
      await feedbackService.submitFeedback(formData)

      toast.success('Feedback submitted successfully! Thank you for your input.')
      closeFeedbackModal()
    } catch (error) {
      logger.error('Error submitting feedback:', error)
      const errorMessage =
        error.response?.data?.error || 'Failed to submit feedback. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isFeedbackModalOpen) return null

  return (
    <ModalPortal>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ display: hideModalForCapture ? 'none' : 'flex' }}
      >
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeFeedbackModal} />

        {/* Modal */}
        <div
          ref={modalRef}
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                Send Feedback
              </h2>
              {contextDescription && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                  <Info className="w-4 h-4" />
                  {contextDescription}
                </p>
              )}
            </div>
            <button
              onClick={closeFeedbackModal}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => setCategory(cat.value)}
                    className={`
                      p-3 text-left rounded-lg border-2 transition-all
                      ${
                        category === cat.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <div className="font-medium text-gray-900 dark:text-gray-100">{cat.label}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {cat.description}
                    </div>
                  </button>
                ))}
              </div>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.category}
                </p>
              )}
            </div>

            {/* Rating (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Overall Experience (Optional)
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                  >
                    <Star
                      className={`
                        w-8 h-8
                        ${
                          rating && star <= rating
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'text-gray-300 dark:text-gray-600 hover:text-yellow-400'
                        }
                      `}
                    />
                  </button>
                ))}
                {rating && (
                  <button
                    type="button"
                    onClick={() => setRating(null)}
                    className="ml-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Your Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                className={`
                  w-full px-4 py-3 rounded-lg border
                  ${
                    errors.message
                      ? 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 dark:border-gray-600 focus:ring-primary-500'
                  }
                  dark:bg-gray-700 dark:text-gray-100
                  focus:outline-none focus:ring-2
                  resize-none
                `}
                placeholder="Please provide detailed feedback... (10-2000 characters)"
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {errors.message && (
                    <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.message}
                    </p>
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">{message.length}/2000</p>
              </div>
            </div>

            {/* Screenshot */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Screenshot (Optional)
              </label>

              {!screenshotPreview ? (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCaptureScreenshot}
                    disabled={isCapturingScreenshot}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Camera className="w-5 h-5" />
                    {isCapturingScreenshot ? 'Capturing...' : 'Capture Screenshot'}
                  </button>

                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors cursor-pointer">
                    <Upload className="w-5 h-5" />
                    Upload Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <div className="relative">
                  <img
                    src={screenshotPreview}
                    alt="Screenshot preview"
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveScreenshot}
                    className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={closeFeedbackModal}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  )
}

export default FeedbackModal
